import { Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import * as dayjs from 'dayjs';
import { AlertifyService } from '@liebherr/old-sage-ui';
import { filter, first, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { AppState } from '../app.models';
import * as fromRouter from '../router';
import { authorizeEffect } from '../shared/operators/authorize-effect';
import { finalizeEffect } from '../shared/operators/finalize-effect';
import * as fromUsers from '../users';
import * as fromFirmwares from './';
import { FirmwareSortColumn } from './../api/core/models/firmware-sort-column';
import { FirmwaresAPI } from './../api/core/services/firmwares-api';

@Injectable()
export class FirmwareEffects {
    routerState$ = this.store.select(fromRouter.selectRouterState);
    loggedInuser$ = this.store.select(fromUsers.selectLoggedInUser);

    loadFirmwares$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(fromFirmwares.loadFirmwares),
            authorizeEffect(this.loggedInuser$, (a, user) => fromFirmwares.getFirmwarePermissions('loadAll')),
            tap(() => this.store.dispatch(fromFirmwares.loading())),
            switchMap((action) => {
                return this.firmwareAPI.getAllFirmwares({}).pipe(
                    map((res) => {
                        return fromFirmwares.loadedFirmwares({ data: res });
                    }),
                    finalizeEffect(
                        () => this.store.dispatch(fromFirmwares.loadingDone()),
                        () =>
                            this.translateService
                                .selectTranslate('ALERT.LOADING_FAILED')
                                .pipe(first())
                                .subscribe((message) => this.alertifyService.log(message, AlertifyService.ERROR, 'bottom left'))
                    )
                );
            })
        );
    });

    addFirmware$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(fromFirmwares.addFirmware),
            authorizeEffect(this.loggedInuser$, (a, user) => fromFirmwares.getFirmwarePermissions('addFirmware')),
            withLatestFrom(this.routerState$),
            filter(([action, routerState]) => routerState?.url.startsWith('/firmwares')),
            tap(() => this.store.dispatch(fromFirmwares.loading())),
            switchMap(([action, routerState]) => {
                return this.firmwareAPI
                    .uploadFirmware({
                        body: {
                            version: action.firmware.version,
                            firmwareFileName: action.firmware.file.name,
                            firmwareFile: action.firmware.file,
                            routerTypeId: action.firmware.routerTypeId,
                        },
                    })
                    .pipe(
                        map((res) => {
                            this.alertifyService.log(
                                this.translateService.translate('ALERT.FIRMWARES.CREATE_SUCCESS'),
                                AlertifyService.SUCCESS,
                                'bottom left'
                            );
                            return fromFirmwares.loadFirmwares();
                        }),
                        finalizeEffect(
                            () => this.store.dispatch(fromFirmwares.loadingDone()),
                            () =>
                                this.alertifyService.log(
                                    this.translateService.translate('ALERT.FIRMWARES.CREATE_FAILED'),
                                    AlertifyService.ERROR,
                                    'bottom left'
                                )
                        )
                    );
            })
        );
    });
    releaseFirmware$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(fromFirmwares.releaseFirmware),
            authorizeEffect(this.loggedInuser$, (a, user) => fromFirmwares.getFirmwarePermissions('releaseFirmware')),
            withLatestFrom(this.routerState$),
            filter(([action, routerState]) => routerState?.url.startsWith('/firmwares')),
            tap(() => this.store.dispatch(fromFirmwares.loading())),
            switchMap(([action, routerState]) => {
                return this.firmwareAPI
                    .releaseFirmware({
                        id: action.id,
                    })
                    .pipe(
                        map(() => {
                            this.alertifyService.log(
                                this.translateService.translate('ALERT.FIRMWARES.RELEASE_SUCCESS'),
                                AlertifyService.SUCCESS,
                                'bottom left'
                            );
                            return fromFirmwares.loadFirmwares();
                        }),
                        finalizeEffect(
                            () => this.store.dispatch(fromFirmwares.loadingDone()),
                            () =>
                                this.alertifyService.log(
                                    this.translateService.translate('ALERT.FIRMWARES.RELEASE_FAILED'),
                                    AlertifyService.ERROR,
                                    'bottom left'
                                )
                        )
                    );
            })
        );
    });
    deleteFirmware$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(fromFirmwares.deleteFirmware),
            authorizeEffect(this.loggedInuser$, (a, user) => fromFirmwares.getFirmwarePermissions('deleteFirmware')),
            withLatestFrom(this.routerState$),
            filter(([action, routerState]) => routerState?.url.startsWith('/firmwares')),
            tap(() => this.store.dispatch(fromFirmwares.loading())),
            switchMap(([action, routerState]) => {
                return this.firmwareAPI
                    .deleteFirmware({
                        id: action.id,
                    })
                    .pipe(
                        map(() => {
                            this.alertifyService.log(
                                this.translateService.translate('ALERT.FIRMWARES.DELETE_SUCCESS'),
                                AlertifyService.SUCCESS,
                                'bottom left'
                            );
                            return fromFirmwares.loadFirmwares();
                        }),
                        finalizeEffect(
                            () => this.store.dispatch(fromFirmwares.loadingDone()),
                            () =>
                                this.alertifyService.log(
                                    this.translateService.translate('ALERT.FIRMWARES.DELETE_FAILED'),
                                    AlertifyService.ERROR,
                                    'bottom left'
                                )
                        )
                    );
            })
        );
    });

    constructor(
        private actions$: Actions,
        private store: Store<AppState>,
        private alertifyService: AlertifyService,
        private translateService: TranslocoService,
        private firmwareAPI: FirmwaresAPI
    ) {}

    private formatDate(date?: Date): string | undefined {
        return date ? dayjs(date).format('YYYY-MM-DD') : undefined;
    }

    private columnStringToColumn(columnKey?: string): FirmwareSortColumn | undefined {
        switch (columnKey) {
            case fromFirmwares.FirmwareColumns.RELEASED_AT: {
                return FirmwareSortColumn.ReleaseDate;
            }
            case fromFirmwares.FirmwareColumns.RELEASED_BY: {
                return FirmwareSortColumn.ReleaseUser;
            }
            default: {
                return undefined;
            }
        }
    }
}
